import type { PlayerState, Equipment } from "@/types/game";
import type { DeckCard } from "@/types/cards";
import { statusDescription } from "@/engine/Status";
import { effectiveAttack, effectiveDefense, effectiveMagicDefense } from "@/engine/damage";
import { effectiveHandLimit } from "@/engine/Equipment";
import { usePanelFx } from "@/hooks/usePanelFx";

export interface PlayerPanelProps {
  player: PlayerState;
  isCurrent: boolean;
  isTargetable?: boolean;
  onTarget?: () => void;
}

interface EquipSlot {
  key: keyof Equipment;
  label: string;
}

const SLOTS: EquipSlot[] = [
  { key: "weapon", label: "武器" },
  { key: "armor", label: "防具" },
  { key: "accessory", label: "饰品" },
];

function statusTitle(name: string, remaining: number): string {
  const desc = statusDescription(name);
  const dur = remaining === -1 ? "永久" : `${remaining}回合`;
  return desc ? `${name}：${desc}（剩余${dur}）` : `${name}（剩余${dur}）`;
}

function heroSkillsTitle(player: PlayerState): string {
  if (!player.hero.skills || player.hero.skills.length === 0) return "";
  return player.hero.skills
    .map((s) => {
      const cost = s.manaCost ? `（耗魔${s.manaCost}）` : "";
      const type = s.type === "active" ? "[主动]" : "[被动]";
      return `${type} ${s.name}${cost}: ${s.description}`;
    })
    .join("\n");
}

function equipTooltip(card: DeckCard | undefined, slot: string): string {
  if (!card) return slot;
  const parts: string[] = [`${card.name}（${slot}）`];
  const bonuses: string[] = [];
  if (card.attackBonus) bonuses.push(`+${card.attackBonus} 攻击`);
  if (card.defenseBonus) bonuses.push(`+${card.defenseBonus} 防御`);
  if (card.magicDefenseBonus) bonuses.push(`+${card.magicDefenseBonus} 魔御`);
  if (card.rangeBonus) bonuses.push(`+${card.rangeBonus} 射程`);
  if (bonuses.length > 0) parts.push(bonuses.join(", "));
  if (card.effectText) parts.push(card.effectText);
  return parts.join("\n");
}

export default function PlayerPanel({
  player,
  isCurrent,
  isTargetable,
  onTarget,
}: PlayerPanelProps) {
  const hpPct =
    player.maxHp > 0
      ? Math.max(0, Math.min(100, (player.hp / player.maxHp) * 100))
      : 0;
  const mpPct =
    player.maxMp > 0
      ? Math.max(0, Math.min(100, (player.mp / player.maxMp) * 100))
      : 0;
  const teamColor = player.team === "A" ? "var(--gold)" : "var(--red)";
  const handLimit = effectiveHandLimit(player);
  const overLimit = player.hand.length > handLimit;
  const fx = usePanelFx(player.id);

  const cls = [
    "player-panel",
    `player-panel--team-${player.team.toLowerCase()}`,
    isCurrent ? "player-panel--current" : "",
    isTargetable ? "player-panel--targetable" : "",
    player.isDying ? "player-panel--dying" : "",
    !player.isAlive ? "player-panel--dead" : "",
    fx.animClass,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      key={fx.animKey || undefined}
      className={cls}
      style={{ borderLeftColor: teamColor }}
      onClick={isTargetable ? onTarget : undefined}
      role={isTargetable ? "button" : undefined}
    >
      {fx.floatDmg != null && (
        <span className={`float-dmg ${fx.floatDmg < 0 ? "float-dmg--dmg" : "float-dmg--heal"}`}>
          {fx.floatDmg > 0 ? "+" : ""}{fx.floatDmg}
        </span>
      )}
      <div className="player-panel__head">
        <span className="player-panel__team-badge" style={{ background: teamColor }}>
          {player.team}
        </span>
        <span className="player-panel__name" style={{ color: teamColor }} title={heroSkillsTitle(player)}>
          {player.hero.name}
        </span>
        {player.isDying && (
          <span className="player-panel__dying-tag">濒死</span>
        )}
        {!player.isAlive && (
          <span className="player-panel__dead-tag">阵亡</span>
        )}
        <span
          className={`player-panel__hand-count${overLimit ? " player-panel__hand-count--over" : ""}`}
          title="手牌/上限"
        >
          手牌 {player.hand.length}/{handLimit}
        </span>
      </div>
      <div className="player-panel__bar">
        <div className="player-panel__bar-label">HP</div>
        <div className="player-panel__bar-track">
          <div
            className="player-panel__bar-fill player-panel__bar-fill--hp"
            style={{ width: `${hpPct}%` }}
          />
        </div>
        <div className="player-panel__bar-num">
          {player.hp}/{player.maxHp}
        </div>
      </div>
      <div className="player-panel__bar">
        <div className="player-panel__bar-label">MP</div>
        <div className="player-panel__bar-track">
          <div
            className="player-panel__bar-fill player-panel__bar-fill--mp"
            style={{ width: `${mpPct}%` }}
          />
        </div>
        <div className="player-panel__bar-num">
          {player.mp}/{player.maxMp}
        </div>
      </div>
      <div className="player-panel__stats" title="当前攻防御（含装备+状态）">
        <span className="player-panel__stat">攻击 {effectiveAttack(player)}</span>
        <span className="player-panel__stat">防御 {effectiveDefense(player)}</span>
        <span className="player-panel__stat">魔御 {effectiveMagicDefense(player)}</span>
      </div>
      <div className="player-panel__equip">
        {SLOTS.map((s) => {
          const c = player.equip[s.key];
          return (
            <div
              key={s.key}
              className={`player-panel__equip-cell${c ? "" : " player-panel__equip-cell--empty"}`}
              title={equipTooltip(c, s.label)}
            >
              {c ? c.name : s.label}
            </div>
          );
        })}
      </div>
      {player.statuses.length > 0 && (
        <div className="player-panel__statuses">
          {player.statuses.map((st) => (
            <span
              key={st.id}
              className="player-panel__status"
              title={statusTitle(st.name, st.remainingTurns)}
            >
              {st.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
